import React, { useState, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Input, FormControl, FormLabel, SimpleGrid,
  IconButton, Box, Flex, Text, Badge, VStack, HStack, Icon, useToast,
  Divider, AlertDialog, AlertDialogOverlay, AlertDialogContent,
  AlertDialogHeader, AlertDialogBody, AlertDialogFooter, Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  FaBook, FaBookOpen, FaLightbulb, FaInfoCircle, FaExclamationTriangle,
  FaBoxOpen, FaTimesCircle, FaTruck, FaShippingFast, FaWallet, FaHandshake,
  FaCog, FaTools, FaWrench, FaCar, FaShoppingCart, FaTags, FaClipboardList,
  FaFileInvoiceDollar, FaChartBar, FaUsers, FaUserTie, FaHeadset, FaPhoneAlt,
  FaEnvelope, FaGlobe, FaStore, FaWarehouse, FaUndo, FaBan, FaCheckCircle,
  FaStar, FaFolderOpen, FaGraduationCap, FaQuestionCircle, FaBell,
  FaCalendarAlt, FaKey, FaLock, FaCamera, FaMoneyBillWave, FaBoxes,
  FaBarcode, FaTruckLoading,
} from 'react-icons/fa';
import { createWikiCategory, updateWikiCategory, deleteWikiCategory } from '../../services/api';

export const WIKI_ICON_MAP = {
  FaBook, FaBookOpen, FaLightbulb, FaInfoCircle, FaExclamationTriangle,
  FaBoxOpen, FaTimesCircle, FaTruck, FaShippingFast, FaWallet, FaHandshake,
  FaCog, FaTools, FaWrench, FaCar, FaShoppingCart, FaTags, FaClipboardList,
  FaFileInvoiceDollar, FaChartBar, FaUsers, FaUserTie, FaHeadset, FaPhoneAlt,
  FaEnvelope, FaGlobe, FaStore, FaWarehouse, FaUndo, FaBan, FaCheckCircle,
  FaStar, FaFolderOpen, FaGraduationCap, FaQuestionCircle, FaBell,
  FaCalendarAlt, FaKey, FaLock, FaCamera, FaMoneyBillWave, FaBoxes,
  FaBarcode, FaTruckLoading,
};

export const resolveWikiIcon = (name) => WIKI_ICON_MAP[name] || FaBook;

export const WIKI_COLOR_OPTIONS = ['gray', 'red', 'orange', 'yellow', 'green', 'teal', 'cyan', 'blue', 'purple', 'pink'];

const EMPTY_FORM = { nome: '', icone: 'FaBook', cor: 'gray' };

const WikiCategoryManager = ({ isOpen, onClose, categories, onChanged }) => {
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const cancelRef = useRef();
  const toast = useToast();

  const rowBg = useColorModeValue('gray.50', 'gray.700');
  const pickerBg = useColorModeValue('gray.50', 'gray.900');

  const startNew = () => { setForm({ ...EMPTY_FORM }); setEditingId(null); };
  const startEdit = (cat) => {
    setForm({ nome: cat.nome, icone: cat.icone || 'FaBook', cor: cat.cor || 'gray' });
    setEditingId(cat.id);
  };
  const cancelForm = () => { setForm(null); setEditingId(null); };

  const handleSave = async () => {
    if (!form.nome.trim()) {
      toast({ title: 'Informe o nome da categoria', status: 'error', position: 'top-right' });
      return;
    }
    setIsSaving(true);
    const payload = { nome: form.nome.trim(), icone: form.icone, cor: form.cor };
    const res = editingId
      ? await updateWikiCategory(editingId, payload)
      : await createWikiCategory(payload);
    setIsSaving(false);

    if (res.success) {
      toast({ title: editingId ? 'Categoria atualizada!' : 'Categoria criada!', status: 'success', position: 'top-right' });
      cancelForm();
      onChanged?.();
    } else {
      toast({ title: res.message, status: 'error', position: 'top-right' });
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    const res = await deleteWikiCategory(toDelete.id);
    if (res.success) {
      toast({ title: 'Categoria excluída.', status: 'info', position: 'top-right' });
      onChanged?.();
    } else {
      toast({ title: res.message, status: 'error', position: 'top-right' });
    }
    setToDelete(null);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Categorias da Wiki</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!form && (
              <VStack align="stretch" spacing={2}>
                {categories.map((cat) => (
                  <Flex key={cat.id} align="center" justify="space-between" p={2} bg={rowBg} borderRadius="md">
                    <HStack spacing={3}>
                      <Flex w="32px" h="32px" align="center" justify="center" borderRadius="md" bg={`${cat.cor || 'gray'}.100`}>
                        <Icon as={resolveWikiIcon(cat.icone)} color={`${cat.cor || 'gray'}.600`} />
                      </Flex>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">{cat.nome}</Text>
                        <Badge colorScheme={cat.cor || 'gray'} fontSize="10px">
                          {cat.topics_count} tópico{cat.topics_count === 1 ? '' : 's'}
                        </Badge>
                      </Box>
                    </HStack>
                    <HStack>
                      <IconButton icon={<EditIcon />} size="xs" variant="ghost" colorScheme="blue" aria-label="Editar" onClick={() => startEdit(cat)} />
                      <Tooltip label={cat.topics_count > 0 ? 'Categoria em uso não pode ser excluída' : ''} fontSize="xs">
                        <IconButton icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red" aria-label="Excluir" isDisabled={cat.topics_count > 0} onClick={() => setToDelete(cat)} />
                      </Tooltip>
                    </HStack>
                  </Flex>
                ))}
                {categories.length === 0 && (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>Nenhuma categoria cadastrada.</Text>
                )}
              </VStack>
            )}

            {form && (
              <VStack align="stretch" spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Nome</FormLabel>
                  <Input
                    size="sm"
                    value={form.nome}
                    autoFocus
                    placeholder="Ex: Garantias"
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Ícone</FormLabel>
                  <Box maxH="160px" overflowY="auto" p={2} bg={pickerBg} borderRadius="md">
                    <SimpleGrid columns={8} spacing={1}>
                      {Object.entries(WIKI_ICON_MAP).map(([name, IconComp]) => (
                        <IconButton
                          key={name}
                          icon={<IconComp />}
                          size="sm"
                          aria-label={name}
                          variant={form.icone === name ? 'solid' : 'ghost'}
                          colorScheme={form.icone === name ? form.cor || 'orange' : 'gray'}
                          onClick={() => setForm({ ...form, icone: name })}
                        />
                      ))}
                    </SimpleGrid>
                  </Box>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Cor</FormLabel>
                  <HStack spacing={1} wrap="wrap">
                    {WIKI_COLOR_OPTIONS.map((color) => (
                      <Box
                        key={color}
                        as="button"
                        type="button"
                        w="26px"
                        h="26px"
                        borderRadius="full"
                        bg={`${color}.400`}
                        border="3px solid"
                        borderColor={form.cor === color ? `${color}.600` : 'transparent'}
                        transform={form.cor === color ? 'scale(1.15)' : 'none'}
                        transition="all 0.15s"
                        onClick={() => setForm({ ...form, cor: color })}
                      />
                    ))}
                  </HStack>
                </FormControl>

                <Divider />

                <Flex align="center" gap={2} p={2} bg={rowBg} borderRadius="md">
                  <Text fontSize="xs" color="gray.500">Prévia:</Text>
                  <Badge colorScheme={form.cor} display="flex" alignItems="center" gap={1} px={2} py={1} borderRadius="full">
                    <Icon as={resolveWikiIcon(form.icone)} boxSize={3} />
                    {form.nome || 'Nova categoria'}
                  </Badge>
                </Flex>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            {!form ? (
              <>
                <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Fechar</Button>
                <Button size="sm" leftIcon={<AddIcon />} colorScheme="orange" onClick={startNew}>Nova Categoria</Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="ghost" mr={2} onClick={cancelForm} isDisabled={isSaving}>Voltar</Button>
                <Button size="sm" colorScheme="orange" onClick={handleSave} isLoading={isSaving}>
                  {editingId ? 'Salvar Alterações' : 'Criar Categoria'}
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={!!toDelete} leastDestructiveRef={cancelRef} onClose={() => setToDelete(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Excluir Categoria</AlertDialogHeader>
            <AlertDialogBody>
              Excluir a categoria "{toDelete?.nome}"?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} size="sm" onClick={() => setToDelete(null)}>Cancelar</Button>
              <Button colorScheme="red" size="sm" onClick={handleDelete} ml={3}>Excluir</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default WikiCategoryManager;
